import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const slides = ['https://cdn.magicpatterns.com/uploads/g4VRep7XLh2wWHzibhjKLe/IMAGEN_1_(2).png', 'https://cdn.magicpatterns.com/uploads/4rg7HFcRKujgptJB16NqhC/CARRUSEL.png', 'https://cdn.magicpatterns.com/uploads/2gAUwkP2skiYd6GwBXTEaR/CARRUSEL_3.png', 'https://cdn.magicpatterns.com/uploads/bFGfS27TEdWS1jm3AaYm9g/CARRUSEL_7.png', 'https://cdn.magicpatterns.com/uploads/7jRWdfivBFdr33D55bECpc/carrusel_8.png', 'https://cdn.magicpatterns.com/uploads/oHjvUtV5CCmBfkhMsp2HY9/carrusel_10.png', 'https://cdn.magicpatterns.com/uploads/k12bWY4jkNAzNVeZFiWxto/CARRUSEL_2.png', 'https://cdn.magicpatterns.com/uploads/nFsTeS9b4r3sm7ZrQWkkus/CARRUSEL_4.png', 'https://cdn.magicpatterns.com/uploads/uuezivjWQRtQvkQsLPXkJV/CARRUSEL_5.png', 'https://cdn.magicpatterns.com/uploads/bv28xvPhmw7igWRPq8Kt9c/CARRUSEL_6.png'];
const AUTOPLAY_MS = 5000;
export function TreatmentsCarousel() {
  const [[index, direction], setState] = useState<[number, number]>([0, 1]);
  const [paused, setPaused] = useState(false);
  const paginate = useCallback((dir: number) => {
    setState(([prev]) => [(prev + dir + slides.length) % slides.length, dir]);
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([prev]) => [next, next > prev ? 1 : -1]);
  }, []);
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => paginate(1), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, paused, paginate]);
  return <motion.div className="mb-12 md:mb-20" initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.2
  }} transition={{
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-464" data-mp="2166bqs4n0ej2">
      <div className="relative overflow-hidden rounded-sm border border-brand-gold/20 bg-brand-dark shadow-xl" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} role="region" aria-roledescription="carrusel" aria-label="Galería de tratamientos en Tairu Aesthetics" data-id="element-465" data-mp="13nmsvv7je4av">
        <div className="relative aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9]" data-id="element-466" data-mp="275t1b5ty4dh3">
          <AnimatePresence initial={false} custom={direction} data-id="element-467" data-mp="1afa6jih444eo">
            <motion.div key={index} className="absolute inset-0" custom={direction} initial={{
            opacity: 0,
            x: direction > 0 ? '18%' : '-18%'
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction > 0 ? '-12%' : '12%'
          }} transition={{
            duration: 0.9,
            ease: [0.22, 1, 0.36, 1]
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.15} onDragEnd={(_, info) => {
            if (info.offset.x < -70) paginate(1);else if (info.offset.x > 70) paginate(-1);
          }} data-id="element-468" data-mp="1agtrvnhml5nb">
              {/* Ken Burns motion */}
              <motion.img src={slides[index]} alt={`Tratamiento en cabina — imagen ${index + 1} de ${slides.length}`} className="h-full w-full object-cover select-none pointer-events-none" initial={{
              scale: 1.12
            }} animate={{
              scale: 1
            }} transition={{
              duration: AUTOPLAY_MS / 1000 + 1.2,
              ease: 'linear'
            }} draggable={false} data-id="element-469" data-mp="5gdn5bow64kn" />
            </motion.div>
          </AnimatePresence>

          {/* Vignette */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/10 to-transparent" data-id="element-470" data-mp="1svh6i1vrn6ze"></div>

          {/* Arrows */}
          <button type="button" onClick={() => paginate(-1)} aria-label="Imagen anterior" className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 h-10 w-10 md:h-12 md:w-12 flex items-center justify-center rounded-full bg-black/40 hover:bg-brand-gold text-white backdrop-blur-sm border border-white/20 transition-colors duration-300" data-id="element-471" data-mp="1iyktpjyu3yp1">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-472" data-mp="10aftunlasxvi" />
          </button>
          <button type="button" onClick={() => paginate(1)} aria-label="Siguiente imagen" className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 h-10 w-10 md:h-12 md:w-12 flex items-center justify-center rounded-full bg-black/40 hover:bg-brand-gold text-white backdrop-blur-sm border border-white/20 transition-colors duration-300" data-id="element-473" data-mp="15768r7ld9nfc">
            <ChevronRightIcon className="w-5 h-5" data-id="element-474" data-mp="7n69lk1kmesz" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-id="element-475" data-mp="lmfsirh5vn0n">
            {slides.map((src, i) => <button key={src} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} aria-current={i === index} className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-8 bg-brand-gold' : 'w-2.5 bg-white/50 hover:bg-white/80'}`} data-id="element-476" data-mp="glrs5f71lttr" data-mpi={i} />)}
          </div>

          {/* Autoplay progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/15 z-20" data-id="element-477" data-mp="1ea1wi0uawto1">
            <motion.div key={`${index}-${paused}`} className="h-full bg-brand-gold" initial={{
            width: '0%'
          }} animate={{
            width: paused ? '0%' : '100%'
          }} transition={{
            duration: paused ? 0 : AUTOPLAY_MS / 1000,
            ease: 'linear'
          }} data-id="element-478" data-mp="19c09j4tr8a7a" />
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="mt-4 flex justify-center gap-2 md:gap-3" data-id="element-479" data-mp="pp3pmm6myzg7">
        {slides.map((src, i) => <button key={src} type="button" onClick={() => goTo(i)} aria-label={`Ver imagen ${i + 1}`} className={`relative h-14 w-12 md:h-20 md:w-16 overflow-hidden rounded-sm border transition-all duration-500 ${i === index ? 'border-brand-gold opacity-100 scale-105' : 'border-brand-gold/20 opacity-60 hover:opacity-100'}`} data-id="element-480" data-mp="unogvw3wu8si" data-mpi={i}>
            <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-481" data-mp="35fpx383w02xr" />
          </button>)}
      </div>
    </motion.div>;
}